import styled from 'styled-components';
import { FlexView } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

// Local neutral palette for a clean, minimal dashboard look (slate tones).
export const UI = {
  bg: '#f5f6f8',
  card: '#ffffff',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  ink: '#0f172a',
  body: '#334155',
  muted: '#94a3b8',
  mutedDark: '#64748b',
  up: '#16a34a',
  down: '#dc2626',
  shadow: '0 1px 3px rgba(0,0,0,.06)',
};

export const Root = styled(FlexView)`
  padding: 24px 28px 48px;
  background: ${UI.bg};
  min-height: 100%;
  gap: 0;
`;

/* Plain anchor: the insights page is a static file in public/, served outside
   the SPA router, so it must be a real <a>, not a router Link. */
export const InsightsLink = styled.a`
  text-decoration: none;
  border-bottom: 1px solid ${UI.border};
  padding-bottom: 1px;

  &:hover {
    border-bottom-color: ${UI.ink};
  }
`;

export const HeaderRow = styled(FlexView)`
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

/* Small pill under the header stating when the underlying Redshift funnel
   tables were last rebuilt (daily Glue job). Hugs its content width. */
export const RefreshNote = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  padding: 4px 10px;
  background: ${UI.card};
  border: 1px solid ${UI.border};
  border-radius: 6px;
`;

/* ── Tabs ── */
export const TabBar = styled(FlexView)`
  flex-direction: row;
  gap: 4px;
  border-bottom: 1px solid ${UI.border};
`;

export const TabItem = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 2px solid ${props => (props.$active ? UI.ink : 'transparent')};
  opacity: ${props => (props.$active ? 1 : 0.6)};
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
`;

/* ── Section + cards ── */
export const SectionLabel = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${UI.mutedDark};
  padding: 28px 0 12px;
`;

/* Bold label for filter fields / chip groups. */
export const FieldLabelText = styled(Text)`
  && {
    font-weight: 700;
    color: ${UI.body};
  }
`;

/* '(unknown)' table cells: dotted underline + help cursor signal that a
   hover reason is available (shown via HoverHint). */
export const UnknownText = styled(Text)`
  && {
    display: inline-block;
    border-bottom: 1px dotted ${UI.muted};
    cursor: help;
  }
`;

/* HoverHint: inline hover target + a fixed-position bubble portaled to
   document.body — table cells clip overflow, so the bubble must escape them. */
export const HintTarget = styled(FlexView)`
  display: inline-flex;
`;

export const HintBubble = styled.div`
  position: fixed;
  left: ${props => props.$left}px;
  top: ${props => props.$top}px;
  max-width: 320px;
  padding: 8px 12px;
  border-radius: 6px;
  background: ${UI.ink};
  color: ${UI.card};
  font-size: 12px;
  line-height: 1.5;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);
  z-index: 2000;
  pointer-events: none;
`;

export const Card = styled(FlexView)`
  background: ${UI.card};
  border-radius: 10px;
  padding: 20px 22px;
  box-shadow: ${UI.shadow};
  gap: 2px;
`;

export const ChartBox = styled(FlexView)`
  position: relative;
  height: ${props => props.$height || 260}px;
  width: 100%;
`;

/* ── Filters ── */
/* Fields align at the TOP (labels are uniform height) so inputs stay in one
   line even when a field has a hint caption below it. */
export const FiltersBar = styled(FlexView)`
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 14px;
  background: ${UI.card};
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: ${UI.shadow};
`;

export const FilterField = styled(FlexView)`
  gap: 6px;
  min-width: ${props => props.$width || '190px'};
  /* Keep the field at its own width in a column context (the period-chips
     card) instead of stretching to the full card width; harmless in the
     Summary row, which already top-aligns its fields. */
  align-self: flex-start;
`;

/* flex:1 + justify-content pushes the buttons to the right edge without
   margins; padding-top drops them level with the inputs (label row height). */
export const ActionsField = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  gap: 12px;
  padding-top: 22px;
`;

/* ── KPI cards ── */
export const KpiRow = styled(FlexView)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  & > * {
    flex: 1 1 160px;
  }
`;

export const KpiCard = styled(FlexView)`
  background: ${UI.card};
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: ${UI.shadow};
  border-left: 3px solid ${props => props.$accent || UI.border};
  gap: 4px;
`;

/* ── Legend ── */
export const Legend = styled(FlexView)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 18px;
  padding: 4px 0 12px;
`;

export const LegendItem = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

export const Dot = styled(FlexView)`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${props => props.$color || UI.muted};
`;

/* ── Tables ── */
/* Tables scroll their BODY only (~10 rows visible) — react-table v6 renders
   .rt-thead / .rt-tbody / .rt-tfoot as siblings, so capping the body keeps the
   header and the Total footer fixed while rows scroll. scrollbar-gutter keeps
   header/body columns aligned when the scrollbar appears. */
export const TableCard = styled(FlexView)`
  background: ${UI.card};
  border-radius: 10px;
  box-shadow: ${UI.shadow};
  overflow: hidden;

  .rt-tbody {
    max-height: 380px;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }
`;

export const Pill = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  color: ${props => props.$fg};
  background: ${props => props.$bg};
`;

export const ShareBarWrap = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const ShareBar = styled(FlexView)`
  height: 5px;
  border-radius: 3px;
  background: ${props => props.$color || '#3b82f6'};
  width: ${props => props.$width || 0}px;
`;

/* ── State blocks ── */
export const StateBlock = styled(FlexView)`
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: ${UI.card};
  border-radius: 10px;
  box-shadow: ${UI.shadow};
  min-height: 160px;
`;

export const EmptyCell = styled(FlexView)`
  align-items: center;
  justify-content: center;
  padding: 28px 12px;
`;

export const CellStack = styled(FlexView)`
  gap: 2px;
`;

/* ── Chips ── */
export const ChipRow = styled(FlexView)`
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const ChipItem = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  padding: 5px 14px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${props => (props.$active ? UI.ink : UI.border)};
  background: ${props => (props.$active ? UI.ink : UI.card)};
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover {
    border-color: ${UI.ink};
  }
`;

export const ChipGroupField = styled(FlexView)`
  gap: 6px;
`;

/* Row of SEPARATE chip groups — wide gap so groups read as distinct filters. */
export const ChipGroupsRow = styled(FlexView)`
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 36px;
`;

/* ── Range picker (react-calendar popover) ── */
/* Needs a real DOM ref for outside-click detection, hence styled.div. */
export const PickerWrap = styled.div`
  position: relative;
`;

export const PickerField = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  background: ${UI.card};
  border: 1px solid ${UI.border};
  border-radius: 8px;
  padding: 9px 12px;
  min-width: 230px;
  cursor: pointer;

  &:hover {
    border-color: ${UI.ink};
  }
`;

export const PickerPop = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
  background: ${UI.card};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  padding: 8px;

  .react-calendar {
    border: none;
    font-family: inherit;
  }
`;

/* ── Multi-select-with-Apply dropdown (frontend variant filter) ── */
/* Reuses PickerWrap / PickerField for the shell. Its own popover, unlike the
   calendar's PickerPop (which react-calendar sizes), must stretch to at least
   the trigger width so it doesn't shrink-wrap narrower than the field. */
export const MSPop = styled(PickerPop)`
  min-width: 100%;
`;

export const MSBar = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 6px 8px;
  border-bottom: 1px solid ${UI.borderLight};
`;

export const MSFooter = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 6px 4px;
  border-top: 1px solid ${UI.borderLight};
`;

/* Small text button (Select all / Clear) — a real <button> keeps it keyboard
   accessible; padding is reset because the app's global reset leaves UA padding. */
export const MSLink = styled.button`
  && {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
    color: ${UI.mutedDark};
    text-decoration: underline;
  }
  &:hover {
    color: ${UI.ink};
  }
`;

export const MSList = styled(FlexView)`
  max-height: 260px;
  overflow-y: auto;
  gap: 2px;
  padding: 6px 0;
`;

export const MSOption = styled(FlexView)`
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${UI.borderLight};
  }
`;

export const MSCheck = styled(FlexView)`
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${props => (props.$active ? UI.ink : UI.border)};
  background: ${props => (props.$active ? UI.ink : UI.card)};
  color: ${UI.card};
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;
`;

/* Full-page blocking overlay while a query is in flight — swallows clicks. */
export const LoadingOverlay = styled(FlexView)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 1000;
  cursor: wait;
`;
